import { createScopedErrors, registerCodes, getCurrentCorrelationId, reportInvalidInput } from '@norkron/web-errors';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { z } from 'zod';
import { Component, createElement, useState, useCallback, useRef, useEffect } from 'react';
import { useThemeVar } from '@norkron/web-themer/react';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

// src/error-codes.ts
var WEB_ERROR_BOUNDARY_LIB = "@norkron/web-error-boundary";
var WEB_ERROR_BOUNDARY_TOKEN = /* @__PURE__ */ Symbol.for("norkron.lib.web-error-boundary");
var { warn, panic } = createScopedErrors(WEB_ERROR_BOUNDARY_LIB, WEB_ERROR_BOUNDARY_TOKEN);
try {
  registerCodes(WEB_ERROR_BOUNDARY_LIB, {
    WEB_ERROR_BOUNDARY_CAUGHT: {
      description: "An <ErrorBoundary> captured a child render failure; the configured fallback was rendered in place of the crashed subtree. The original error is forwarded to the @norkron/errors sink with a correlation id.",
      severity: "warning",
      docsAnchor: "web-error-boundary/caught",
      exampleFix: "Inspect the captured error and fix the underlying child crash. Use the correlation id to cross-reference logs."
    },
    WEB_ERROR_BOUNDARY_INVALID_INPUT: {
      description: "A public input to @norkron/web-error-boundary failed zod validation. Inspect payload.fieldPath + payload.expected; pass input matching the schema exported by this lib.",
      severity: "warning",
      docsAnchor: "https://norkron.dev/web-errors/WEB_INVALID_INPUT_SHAPE",
      exampleFix: "Match the per-export schema (ErrorBoundaryPropsSchema, DefaultErrorFallbackPropsSchema, AsyncErrorBoundaryPropsSchema)."
    },
    EB_SILENT_RECOVERY: {
      description: "selfCheck found an error-boundary instance that recovered without surfacing a user-visible fallback. Silent recovery hides crashes from operators.",
      severity: "warning",
      docsAnchor: "web-error-boundary/self-check#silent-recovery",
      exampleFix: "Render a visible fallback (e.g. <DefaultErrorFallback />) or surface a toast/banner on recovery."
    },
    EB_SELFCHECK_BUDGET_EXCEEDED: {
      description: "selfCheck exceeded its per-call time budget while walking error-boundary instances; partial results returned.",
      severity: "info",
      docsAnchor: "web-error-boundary/self-check#budget-exceeded",
      exampleFix: "Increase budgetMs or reduce the number of error-boundary instances per page."
    },
    EB_SELFCHECK_INTERNAL_ERROR: {
      description: "selfCheck threw internally while inspecting an error-boundary instance. Exception caught + reported as a finding.",
      severity: "info",
      docsAnchor: "web-error-boundary/self-check#internal-error",
      exampleFix: "Inspect finding.message + finding.extras for the underlying error."
    }
  });
} catch {
}
var SelfCheckOptionsSchema = z.object({
  budgetMs: z.number().int().positive().max(5e3).optional(),
  minSeverity: z.enum(["critical", "major", "minor", "info"]).optional(),
  locale: z.string().min(2).max(35).optional()
}).strict();

// src/validate-self-check.ts
var DEFAULT_BUDGET_MS = 50;
function parseSelfCheckOptions(opts) {
  const parsed = SelfCheckOptionsSchema.safeParse(opts ?? {});
  if (parsed.success) {
    return {
      budgetMs: parsed.data.budgetMs ?? DEFAULT_BUDGET_MS,
      minSeverity: parsed.data.minSeverity ?? "info"
    };
  }
  return { budgetMs: DEFAULT_BUDGET_MS, minSeverity: "info" };
}

// src/self-check.ts
var LIB_ID = "@norkron/web-error-boundary";
var SEVERITY_RANK = {
  info: 0,
  minor: 1,
  major: 2,
  critical: 3
};
function meetsFloor(finding, floor) {
  return SEVERITY_RANK[finding.severity] >= SEVERITY_RANK[floor];
}
function selectorFor(el) {
  const testid = el.getAttribute("data-testid");
  if (testid) return `[data-testid="${testid}"]`;
  const id = el.getAttribute("id");
  if (id) return `#${id}`;
  const tag = el.tagName.toLowerCase();
  const cls = el.getAttribute("class")?.split(/\s+/).filter(Boolean).slice(0, 2).join(".");
  return cls ? `${tag}.${cls}` : tag;
}
function hasVisibleChrome(el) {
  const text = (el.textContent ?? "").trim();
  if (text.length > 0) return true;
  const interactive = el.querySelector("button, a, [role=button], [role=alert], [role=status]");
  return interactive !== null;
}
function selfCheck(root, opts) {
  const start = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
  const safeOpts = parseSelfCheckOptions(opts);
  const findings = [];
  try {
    const caught = Array.from(
      root.querySelectorAll(
        '[data-norkron-lib^="@norkron/web-error-boundary"][data-error-boundary-state="caught"]'
      )
    );
    for (const inst of caught) {
      const now = typeof performance !== "undefined" && typeof performance.now === "function" ? performance.now() : Date.now();
      if (now - start > safeOpts.budgetMs) {
        findings.push({
          libId: LIB_ID,
          code: "EB_SELFCHECK_BUDGET_EXCEEDED",
          category: "perf",
          severity: "info",
          message: `selfCheck exceeded budget ${safeOpts.budgetMs}ms; partial results returned.`,
          extras: { budgetMs: safeOpts.budgetMs, elapsed: now - start }
        });
        break;
      }
      if (!hasVisibleChrome(inst)) {
        findings.push({
          libId: LIB_ID,
          code: "EB_SILENT_RECOVERY",
          category: "flow",
          severity: "major",
          message: "ErrorBoundary caught an error but the recovery state has zero visible chrome \u2014 the user sees a blank region.",
          selector: selectorFor(inst),
          suggestedFix: "Render a visible fallback (status text, retry CTA, error icon) so the user knows recovery happened."
        });
      }
    }
  } catch (err) {
    findings.push({
      libId: LIB_ID,
      code: "EB_SELFCHECK_INTERNAL_ERROR",
      category: "identity",
      severity: "info",
      message: `selfCheck threw internally: ${err instanceof Error ? err.message : String(err)}`
    });
  }
  return findings.filter((f) => meetsFloor(f, safeOpts.minSeverity));
}

// src/version.ts
var LIB_VERSION = "0.3.6";
z.unknown().refine((v) => typeof v === "function", { message: "expected function" });
z.object({
  componentStack: z.string(),
  correlationId: z.string(),
  lib: z.literal("@norkron/web-error-boundary"),
  fn: z.literal("ErrorBoundary"),
  kind: z.literal("contract"),
  errorCause: z.unknown(),
  timestamp: z.number()
});
var ErrorBoundaryPropsSchema = z.object({
  children: z.unknown().optional(),
  // `fallback` is either a ReactNode (anything) or a render function. We
  // allow `unknown` here because narrowing ReactNode-vs-function lives in TS,
  // and rejecting valid ReactNode trees would produce false positives.
  fallback: z.unknown(),
  onError: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function or undefined"
  }).optional(),
  resetKeys: z.array(z.unknown()).optional(),
  onReset: z.unknown().refine((v) => v === void 0 || typeof v === "function", {
    message: "expected function or undefined"
  }).optional()
});
var DefaultErrorFallbackPropsSchema = z.object({
  error: z.unknown().refine(
    (v) => v instanceof Error || typeof v === "object" && v !== null && "message" in v,
    { message: "expected Error or error-like { message }" }
  ),
  reset: z.unknown().refine((v) => typeof v === "function", { message: "expected function" }),
  correlationId: z.string().optional(),
  title: z.string().optional(),
  className: z.string().optional()
});
var AsyncErrorBoundaryPropsSchema = ErrorBoundaryPropsSchema.extend({
  captureUnhandledRejections: z.boolean().optional()
});

// src/correlation.ts
function synthesizeCorrelationId() {
  const a = Math.floor(Math.random() * 4294967295).toString(36);
  const b = Math.floor(Math.random() * 4294967295).toString(36);
  const t = typeof performance !== "undefined" && typeof performance.now === "function" ? Math.floor(performance.now() * 1e3).toString(36) : Date.now().toString(36);
  return `cor_${t}${a}${b}`;
}
function makeCorrelationId() {
  const ctx = getCurrentCorrelationId();
  if (typeof ctx === "string" && ctx.length > 0) {
    return ctx.startsWith("cor_") ? ctx : `cor_${ctx}`;
  }
  return synthesizeCorrelationId();
}

// src/zod-bridge.ts
function toZodLikeSafeParseError(failure) {
  return failure;
}

// src/validate-input.ts
function validateErrorBoundaryProps(props) {
  const parsed = ErrorBoundaryPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_ERROR_BOUNDARY_LIB,
      libToken: WEB_ERROR_BOUNDARY_TOKEN,
      fn: "ErrorBoundary",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateDefaultErrorFallbackProps(props) {
  const parsed = DefaultErrorFallbackPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_ERROR_BOUNDARY_LIB,
      libToken: WEB_ERROR_BOUNDARY_TOKEN,
      fn: "DefaultErrorFallback",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}
function validateAsyncErrorBoundaryProps(props) {
  const parsed = AsyncErrorBoundaryPropsSchema.safeParse(props);
  if (!parsed.success) {
    reportInvalidInput({
      lib: WEB_ERROR_BOUNDARY_LIB,
      libToken: WEB_ERROR_BOUNDARY_TOKEN,
      fn: "AsyncErrorBoundary",
      parseResult: toZodLikeSafeParseError(parsed)
    });
  }
}

// src/ErrorBoundary.tsx
function shallowArrayEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }
  return true;
}
function toError(value) {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  try {
    return new Error(`Non-Error thrown: ${JSON.stringify(value)}`);
  } catch {
    return new Error("Non-Error thrown (unserializable)");
  }
}
function isFallbackRender(fallback) {
  return typeof fallback === "function";
}
var ErrorBoundary = class extends Component {
  state = { error: null, correlationId: null };
  constructor(props) {
    super(props);
    validateErrorBoundaryProps(props);
    this.reset = this.reset.bind(this);
  }
  static getDerivedStateFromError(error) {
    return { error: toError(error), correlationId: makeCorrelationId() };
  }
  componentDidCatch(rawError, info) {
    const error = toError(rawError);
    const correlationId = this.state.correlationId ?? makeCorrelationId();
    const errorInfo = {
      componentStack: info.componentStack ?? "",
      correlationId,
      lib: "@norkron/web-error-boundary",
      fn: "ErrorBoundary",
      kind: "contract",
      errorCause: rawError,
      timestamp: Date.now()
    };
    try {
      warn({
        fn: "ErrorBoundary",
        code: "WEB_ERROR_BOUNDARY_CAUGHT",
        message: `<ErrorBoundary> caught: ${error.message}`,
        severity: "warning",
        // Per-correlation-id dedup so distinct catches in the same
        // process each surface; the default lib:fn:code key would
        // suppress every catch after the first one.
        dedupKey: `${WEB_ERROR_BOUNDARY_LIB}:ErrorBoundary:WEB_ERROR_BOUNDARY_CAUGHT:${correlationId}`,
        errorCause: rawError
      });
    } catch {
    }
    try {
      this.props.onError?.(error, errorInfo, correlationId);
    } catch {
    }
  }
  componentDidUpdate(prevProps) {
    if (this.state.error === null) return;
    if (!shallowArrayEqual(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }
  reset() {
    if (this.state.error === null) return;
    this.setState({ error: null, correlationId: null });
    try {
      this.props.onReset?.();
    } catch {
    }
  }
  render() {
    if (this.state.error !== null) {
      const { fallback } = this.props;
      const rendered = isFallbackRender(fallback) ? fallback(this.state.error, this.reset) : fallback;
      return createElement(
        "div",
        {
          "data-norkron-lib": `@norkron/web-error-boundary@${LIB_VERSION}`,
          "data-error-boundary-state": "caught",
          style: { display: "contents" }
        },
        rendered
      );
    }
    return this.props.children;
  }
};
function DefaultErrorFallback(props) {
  validateDefaultErrorFallbackProps(props);
  const {
    error,
    reset,
    correlationId,
    title = "Something went wrong",
    className
  } = props;
  const [copied, setCopied] = useState(false);
  const iconColor = useThemeVar("--error-boundary-icon-color", "#dc2626");
  const onCopy = useCallback(() => {
    if (!correlationId) return;
    try {
      navigator.clipboard?.writeText(correlationId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
    }
  }, [correlationId]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: ["error-boundary", "error-boundary__fallback", className].filter(Boolean).join(" "),
      "data-testid": "error-boundary__fallback",
      role: "alert",
      "aria-live": "polite",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "error-boundary__header", children: [
          /* @__PURE__ */ jsx(
            "span",
            {
              "aria-hidden": "true",
              className: "error-boundary__icon",
              style: { color: iconColor },
              children: "\u26A0"
            }
          ),
          /* @__PURE__ */ jsx("h3", { className: "error-boundary__title", children: /* @__PURE__ */ jsx("span", { className: "error-boundary__title-text", children: title }) })
        ] }),
        /* @__PURE__ */ jsx("p", { className: "error-boundary__message", "data-testid": "error-boundary__message", children: /* @__PURE__ */ jsx("span", { className: "error-boundary__message-text", children: error.message || "An unexpected error occurred." }) }),
        correlationId && /* @__PURE__ */ jsxs("div", { className: "error-boundary__correlation", children: [
          /* @__PURE__ */ jsx("span", { className: "error-boundary__correlation-label", children: /* @__PURE__ */ jsx("span", { className: "error-boundary__correlation-label-text", children: "Reference:" }) }),
          /* @__PURE__ */ jsx(
            "code",
            {
              "data-testid": "error-boundary__correlation-id",
              className: "error-boundary__correlation-id",
              children: correlationId
            }
          ),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              "data-testid": "error-boundary__copy",
              onClick: onCopy,
              className: [
                "error-boundary__copy",
                copied ? "error-boundary__copy--copied" : ""
              ].filter(Boolean).join(" "),
              children: /* @__PURE__ */ jsx("span", { className: "error-boundary__copy-label", children: copied ? "Copied" : "Copy" })
            }
          )
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            "data-testid": "error-boundary__retry",
            onClick: reset,
            className: "error-boundary__retry",
            children: /* @__PURE__ */ jsx("span", { className: "error-boundary__retry-label", children: "Try again" })
          }
        )
      ]
    }
  );
}
function useErrorHandler() {
  const [, setState] = useState(null);
  return useCallback(
    (error) => {
      setState(() => {
        if (error instanceof Error) throw error;
        if (typeof error === "string") throw new Error(error);
        try {
          throw new Error(`Non-Error thrown: ${JSON.stringify(error)}`);
        } catch (e) {
          if (e instanceof Error && e.message.startsWith("Non-Error thrown:")) throw e;
          throw new Error("Non-Error thrown (unserializable)");
        }
      });
    },
    [setState]
  );
}
function UnhandledRejectionForwarder({ children }) {
  const handle = useErrorHandler();
  const handleRef = useRef(handle);
  handleRef.current = handle;
  useEffect(() => {
    if (typeof window === "undefined") return;
    const listener = (evt) => {
      const reason = evt.reason;
      handleRef.current(reason ?? new Error("Unhandled promise rejection"));
    };
    window.addEventListener("unhandledrejection", listener);
    return () => {
      window.removeEventListener("unhandledrejection", listener);
    };
  }, []);
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function AsyncErrorBoundary(props) {
  validateAsyncErrorBoundaryProps(props);
  const {
    children,
    captureUnhandledRejections = true,
    ...rest
  } = props;
  return /* @__PURE__ */ jsx(ErrorBoundary, { ...rest, children: captureUnhandledRejections ? /* @__PURE__ */ jsx(UnhandledRejectionForwarder, { children }) : children });
}

// src/index.ts
try {
  registerLazy(
    "@norkron/web-error-boundary",
    () => selfCheck
  );
} catch {
}

export { AsyncErrorBoundary, DefaultErrorFallback, ErrorBoundary, LIB_VERSION, makeCorrelationId, selfCheck, useErrorHandler };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map